import {Locale} from '@/utils/constants'
import {getRouteToPrivacyPolicy, routeTo} from '@/utils/router'

export enum CookiesType {
  necessary = 'necessary',
  other = 'other',
}

export type CookiesTableType = {
  type: string
  name: string
  purpose: string
  expiration: string
  address: string[]
  links: {
    linkText: string
    linkHref: string
    openInNewTab: boolean
  }[]
}

const tramaAddress = [
  'cookies.table-address-trama-0',
  'cookies.table-address-trama-1',
  'cookies.table-address-trama-2',
  'cookies.table-address-trama-3',
]

const googleAddress = [
  'cookies.table-address-google-0',
  'cookies.table-address-google-1',
  'cookies.table-address-google-2',
  'cookies.table-address-google-3',
]

const facebookAddress = [
  'cookies.table-address-facebook-0',
  'cookies.table-address-facebook-1',
  'cookies.table-address-facebook-2',
  'cookies.table-address-facebook-3',
]

const getNecessaryCookiesTable = (language: Locale): CookiesTableType[] => [
  {
    type: 'cookies.table-necessary-for-operation',
    name: 'cookies.table-necessary-name-0',
    purpose: 'cookies.table-necessary-purpose-0',
    expiration: 'cookies.table-exp-2h',
    address: tramaAddress,
    links: [
      {
        linkText: 'cookies.table-more-info',
        linkHref: getRouteToPrivacyPolicy(language),
        openInNewTab: false,
      },
    ],
  },
  {
    type: 'cookies.table-necessary-for-operation',
    name: 'cookies.table-necessary-name-1',
    purpose: 'cookies.table-necessary-purpose-1',
    expiration: 'cookies.table-exp-2h',
    address: tramaAddress,
    links: [
      {
        linkText: 'cookies.table-more-info',
        linkHref: getRouteToPrivacyPolicy(language),
        openInNewTab: false,
      },
    ],
  },
  {
    type: 'cookies.table-necessary-for-operation',
    name: 'cookies.table-necessary-name-2',
    purpose: 'cookies.table-necessary-purpose-2',
    expiration: 'cookies.table-exp-12m',
    address: tramaAddress,
    links: [
      {
        linkText: 'cookies.table-more-info',
        linkHref: getRouteToPrivacyPolicy(language),
        openInNewTab: false,
      },
    ],
  },
]

const otherCookiesTable: CookiesTableType[] = [
  {
    type: 'cookies.table-analytics',
    name: 'cookies.table-other-name-0',
    purpose: 'cookies.table-other-purpose-0',
    expiration: 'cookies.table-exp-24m',
    address: googleAddress,
    links: [
      {
        linkText: 'cookies.table-more-info',
        linkHref: 'https://support.google.com/analytics/topic/2919631?hl=sk&ref_topic=1008008',
        openInNewTab: true,
      },
      {
        linkText: 'cookies.table-turn-off',
        linkHref: 'https://adssettings.google.com/authenticated',
        openInNewTab: true,
      },
    ],
  },
  {
    type: 'cookies.table-remarketing',
    name: 'cookies.table-other-name-1',
    purpose: 'cookies.table-other-purpose-1',
    expiration: 'cookies.table-exp-24m',
    address: googleAddress,
    links: [
      {
        linkText: 'cookies.table-more-info',
        linkHref: 'https://support.google.com/analytics/topic/2919631?hl=sk&ref_topic=1008008',
        openInNewTab: true,
      },
      {
        linkText: 'cookies.table-turn-off',
        linkHref: 'https://adssettings.google.com/authenticated',
        openInNewTab: true,
      },
    ],
  },
  {
    type: 'cookies.table-remarketing',
    name: 'cookies.table-other-name-2',
    purpose: 'cookies.table-other-purpose-2',
    expiration: 'cookies.table-exp-3m',
    address: facebookAddress,
    links: [
      {
        linkText: 'cookies.table-more-info',
        linkHref: 'https://www.facebook.com/about/privacy',
        openInNewTab: true,
      },
      {
        linkText: 'cookies.table-turn-off',
        linkHref: 'https://www.facebook.com/ads/preferences/?entry_product=ad_settings_screen',
        openInNewTab: true,
      },
    ],
  },
]

export const getCookiesContent = (locale: Locale) => {
  return {
    metaTitle: 'cookies.meta-title',
    metaDescription: 'cookies.meta-description',
    hero: {
      title: 'cookies.hero-title',
      subtitle: 'cookies.hero-subtitle',
    },
    // start of common between cookies modal (accessible everywhere) and cookies page
    banner: {
      ariaLabel: 'cookies-banner-aria-label',
      description: 'cookies-description',
      infoLinkHref: routeTo.cookies(),
      buttonConsentTitle: 'button-consent',
      buttonRejectTitle: 'button-reject-consent',
    },
    types: {
      [CookiesType.necessary]: {
        title: 'cookies-necessary-title',
        description: 'cookies-necessary-description',
        table: getNecessaryCookiesTable(locale),
      },
      [CookiesType.other]: {
        title: 'cookies-other-title',
        description: 'cookies-other-description',
        table: otherCookiesTable,
      },
    },
    allowedTitle: 'cookies-allowed-title',
    forbiddenTitle: 'cookies-forbidden-title',
    modal: {
      title: 'cookies-personal-settings',
      buttonSaveTitle: 'button-save-settings',
      buttonAcceptTitle: 'button-accept-all',
    },
    // end of common
    settingsTitle: 'cookies.settings-title',
    faqs: [
      {
        question: 'cookies.faq-question-0',
        answer: 'cookies.faq-answer-0',
      },
      {
        question: 'cookies.faq-question-1',
        answer: 'cookies.faq-answer-1',
      },
      {
        question: 'cookies.faq-question-2',
        answer: 'cookies.faq-answer-2',
      },
      {
        question: 'cookies.faq-question-3',
        answer: 'cookies.faq-answer-3',
      },
    ],
    tableTypeTitle: 'cookies.table-header-0',
    tableNameTitle: 'cookies.table-header-1',
    tablePurposeTitle: 'cookies.table-header-2',
    tableExpirationTitle: 'cookies.table-header-3',
    tableAddressTitle: 'cookies.table-header-4',
    tableLinkTitle: 'cookies.table-header-5',
    options: {
      title: 'cookies.options-title',
      subtitle: 'cookies.options-subtitle',
      list: [
        {
          option: 'cookies.options-option-0',
        },
        {
          option: 'cookies.options-option-1',
          linkHref: 'https://optout.aboutads.info',
          linkText: 'https://optout.aboutads.info',
        },
        {
          option: 'cookies.options-option-2',
        },
      ],
      buttonLinkHref: getRouteToPrivacyPolicy(locale),
      buttonLinkText: 'cookies.options-gdpr-link',
    },
  }
}
